import { JobRating } from "./JobOrder";
import { ChecklistTask } from "./ChecklistTask";
import { MarinerProfile } from "./MarinerProfile";
import { TravelDocument } from "./TravelDocument";
import { User } from "./User";
import { Vessel } from "./Vessel";
import { ProfileDocumentType } from './ProfileDocument';

export type trainingOrderSearchParams = {
    assigneeId: string | undefined;
    rating: JobRating | undefined;
    compatibleWithRating: JobRating | undefined;
    trainingOrderId: string | undefined;
    scheduled: boolean | undefined;
    completed: boolean | undefined;
    deleted: boolean | undefined;
    requestedByCaptain: boolean | undefined;
    modifiedByCaptain: boolean | undefined;
    profileId: string | undefined;
    page: number;
    size: number;
    sortColumn: string;
    sortDirection: "asc" | "desc" | undefined;
  };

export type TrainingInsightView = {
    profile: MarinerProfile;
    training: Training;
    documentType: ProfileDocumentType;
    onboard: boolean;
    expirationDate: string;
}

export type TrainingInsightsView = {
    expiredTrainingInsights: TrainingInsightView[];
    neededTrainingInsights: TrainingInsightView[];
    pendingTrainingInsights: TrainingInsightView[];
    documentInsights: TrainingInsightView[];
}

export type Training = {
    id?: number;
    name: string | undefined;
    validityInMonths: number | undefined;
    deleted: boolean | undefined;
}

export type trainingCreateParams = {
    name: string | undefined;
    validityInMonths: number | undefined;
}

export type TrainingLocation = {
    id?: number;
    name: string | undefined;
}

export type TrainingOrderMetadata = {
    expiredCount: number;
    trainingNeededCount: number;
    pendingConfirmationCount: number;
    completedCount: number;
    documentsExpiredCount: number;
}

export enum TrainingOrderStatus {
    EXPIRED = "EXPIRED",
    TRAINING_NEEDED = "TRAINING_NEEDED",
    PENDING_CONFIRMATION = "PENDING_CONFIRMATION",
    COMPLETED = "COMPLETED",
    SCHEDULED = "SCHEDULED",
    REQUESTED = "REQUESTED"
}

export const TrainingOrderStatusLabel: { [key in TrainingOrderStatus]: string } = {
    [TrainingOrderStatus.COMPLETED]: "Completed",
    [TrainingOrderStatus.EXPIRED]: "Expired",
    [TrainingOrderStatus.PENDING_CONFIRMATION]: "Pending Confirmation",
    [TrainingOrderStatus.TRAINING_NEEDED]: "Training Needed",
    [TrainingOrderStatus.SCHEDULED]: "Scheduled",
    [TrainingOrderStatus.REQUESTED]: "Requested",
}

export type TrainingOrder = {
    id?: number;
    createdDate: string;
    dueDate: string;
    startDate: string | undefined;
    endDate: string | undefined;
    daysToBePaid: string | undefined;
    vessel: Vessel | undefined;
    assignee?: User;
    assigneeId?: string;
    trainingLocation: TrainingLocation | undefined;
    locationId?: number;
    status: TrainingOrderStatus | undefined;
    comments: string;
    mariner: MarinerProfile | undefined;
    marinerLeavingFrom: string | undefined;
    marinerDestination: string | undefined;
    marinerTravelTimestamp: string | undefined; // string to bind directly to time input element
    marinerAccomodation: string | undefined;
    marinerPickupDate: string | undefined;
    marinerDropoffDate: string | undefined;
    marinerNotes: string | undefined;
    training: Training | undefined;
    trainingId?: number;
    checklistRoot: ChecklistTask | undefined;
    travelDocuments: TravelDocument[] | undefined;
    rating: JobRating;
    flightNotice: boolean | undefined;
    note: string | undefined;
    completionDate?: string | undefined;
    expirationDate?: string | undefined;
    requestByCaptain?: boolean;
    modifiedByCaptain?: boolean;
}

export type TrainingOrderChange = {
    id: number;
    actionTimestamp: Date | undefined;
    actionById: string | undefined;
    actionByName: string | undefined;
    actionByEmail: string | undefined;
    fieldName: TrainingOrderFieldName;
    logType: String;
    oldVal?: string | undefined;
    newVal?: string | undefined;
};

export enum TrainingOrderFieldName {
    empty = "",
    trainingOrder = "trainingOrder",
    assignee_id = "assignee_id",
    comments = "comments",
    completion_date = "completion_date",
    created_date = "created_date",
    days_to_be_paid = "days_to_be_paid",
    deleted = "deleted",
    due_date = "due_date",
    end_date = "end_date",
    expiration_date = "expiration_date",
    flight_notice = "flight_notice",
    last_updated = "last_updated",
    location_id = "location_id",
    note = "note",
    mariner_accomodation = "mariner_accomodation",
    mariner_destination = "mariner_destination",
    mariner_dropoff_date = "mariner_dropoff_date",
    mariner_id = "mariner_id",
    offsigner_leaving_from = "mariner_leaving_from",
    mariner_notes = "mariner_notes",
    mariner_pickup_date = "mariner_pickup_date",
    mariner_travel_timestamp = "mariner_travel_timestamp",
    mariner_onboarded = "mariner_onboarded",
    rating = "rating",
    start_date = "start_date",
    status = "status",
    training_id = "training_id",
    unopened_training_order = "unopened_training_order",
    checklist_root_id = "checklist_root_id",
    vessel_id = "vessel_id",
    action_by_id = "action_by_id",
    issue_date = "issue_date",
    type = "type",
    extra = "extra",
    publish = "publish",
    smis = "smis",
    requestByCaptain = "request_by_captain",
    modifiedByCaptain = "modified_by_captain"
}
  
export const TrainingOrderFieldNameLabel: { [key in TrainingOrderFieldName]: string } = {
    [TrainingOrderFieldName.empty]: "",
    [TrainingOrderFieldName.trainingOrder]: "Training Order",
    [TrainingOrderFieldName.assignee_id]: "Assignee",
    [TrainingOrderFieldName.comments]: "Comments",
    [TrainingOrderFieldName.completion_date]: "Completion Date",
    [TrainingOrderFieldName.created_date]: "Created Date",
    [TrainingOrderFieldName.days_to_be_paid]: "Days to be Paid",
    [TrainingOrderFieldName.deleted]: "Deleted",
    [TrainingOrderFieldName.due_date]: "Due Date",
    [TrainingOrderFieldName.end_date]: "End Date",
    [TrainingOrderFieldName.expiration_date]: "Expiration Date",
    [TrainingOrderFieldName.flight_notice]: "Flight Notice",
    [TrainingOrderFieldName.last_updated]: "Last Updated",
    [TrainingOrderFieldName.location_id]: "Location",
    [TrainingOrderFieldName.note]: "Order Notes",
    [TrainingOrderFieldName.mariner_accomodation]: "Accomodation",
    [TrainingOrderFieldName.mariner_destination]: "Destination",
    [TrainingOrderFieldName.mariner_dropoff_date]: "Drop Off Date",
    [TrainingOrderFieldName.mariner_id]: "Mariner",
    [TrainingOrderFieldName.offsigner_leaving_from]: "Leaving From",
    [TrainingOrderFieldName.mariner_notes]: "Mariner Notes",
    [TrainingOrderFieldName.mariner_pickup_date]: "Pick Up Date",
    [TrainingOrderFieldName.mariner_travel_timestamp]: "Travel Timestamp",
    [TrainingOrderFieldName.mariner_onboarded]: "Onboarded",
    [TrainingOrderFieldName.rating]: "Rating",
    [TrainingOrderFieldName.start_date]: "Start Date",
    [TrainingOrderFieldName.status]: "Status",
    [TrainingOrderFieldName.training_id]: "Training",
    [TrainingOrderFieldName.unopened_training_order]: "Unopened",
    [TrainingOrderFieldName.checklist_root_id]: "Checklist ROot",
    [TrainingOrderFieldName.vessel_id]: "Vessel",
    [TrainingOrderFieldName.action_by_id]: "Action By",
    [TrainingOrderFieldName.issue_date]: "Issue Date",
    [TrainingOrderFieldName.type]: "Type",
    [TrainingOrderFieldName.extra]: "Extra",
    [TrainingOrderFieldName.publish]: "Publish",
    [TrainingOrderFieldName.smis]: "SMIS",
    [TrainingOrderFieldName.requestByCaptain]: "Request By Captain",
    [TrainingOrderFieldName.modifiedByCaptain]: "Modified By Captain"
};

export type TrainingReport =  {
  marinerCount: number,
  courseCount: number,
  daysPaidCount: number
}
  